"use client";

import Title from "@/components/common/Typography/Title";
import { useTERouter } from "@/hooks/app/useTERouter";
import { useEsimFeaturedCollections } from "@/hooks/esim/useEsimFeaturedCollections";
import { EsimCollection } from "@/lib/redux/features/esim/types";
import { getEsimPackageUnitPrice } from "@/utils/esim/esimPackagePrice";
import { getFormatCurrency } from "@/utils/string/formatCurrency";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card, Col, Row, Skeleton } from "antd";
import { FC } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

/** Thumbnail sẽ được CMS bổ sung vào `metaData.thumbnail` — chưa có thì fallback icon. */
const getCollectionThumbnail = (collection: EsimCollection): string | null => {
  const thumbnail = (collection.metaData as { thumbnail?: string } | null)?.thumbnail;
  return typeof thumbnail === "string" && thumbnail.trim() ? thumbnail : null;
};

/** `esim[0]` — collection fetch với `esimLimit: 1` (xem `useEsimFeaturedCollections`), và catalog LUÔN
 * sắp theo `price` tăng dần (api_guide mục 1.1) nên đây chính là gói rẻ nhất của collection. */
const getStartingPriceLabel = (collection: EsimCollection, t: TFunction): string | null => {
  const cheapestEsim = collection.esim?.[0];
  if (!cheapestEsim || cheapestEsim.price == null) return null;
  return `${t("esim.from_price")} ${getFormatCurrency(getEsimPackageUnitPrice(cheapestEsim))}`;
};

const SKELETON_CARD_COUNT = 4;

const EsimFeaturedCollections: FC = () => {
  const { t } = useTranslation();
  const router = useTERouter();
  const { collections, loading } = useEsimFeaturedCollections(6);

  if (loading) {
    return (
      <section className="esim-hub__popular" aria-hidden>
        <Title level={3} style={{ marginTop: 0 }}>
          {t("esim.featured_collections_title")}
        </Title>
        <Row gutter={[16, 16]}>
          {Array.from({ length: SKELETON_CARD_COUNT }).map((_, idx) => (
            <Col xs={12} sm={8} md={6} key={idx}>
              <Card className="esim-featured-collection-card">
                <Skeleton.Image active style={{ width: "100%", height: 120 }} />
                <Skeleton active title paragraph={{ rows: 2 }} style={{ marginTop: 12 }} />
              </Card>
            </Col>
          ))}
        </Row>
      </section>
    );
  }

  if (!collections.length) return null;

  const goToCollection = (collection: EsimCollection) => {
    router.push(
      `/esim-du-lich/ket-qua-tim-kiem?collectionId=${collection.id}&collectionName=${encodeURIComponent(collection.displayName)}`,
    );
  };

  return (
    <section className="esim-hub__popular" aria-labelledby="esim-featured-collections-heading">
      <Title id="esim-featured-collections-heading" level={3} style={{ marginTop: 0 }}>
        {t("esim.featured_collections_title")}
      </Title>
      <Row gutter={[16, 16]}>
        {collections.map((collection) => {
          const thumbnail = getCollectionThumbnail(collection);
          const priceLabel = getStartingPriceLabel(collection, t);
          return (
            <Col xs={12} sm={8} md={6} key={collection.id}>
              <Card
                hoverable
                onClick={() => goToCollection(collection)}
                className="esim-featured-collection-card"
                cover={
                  <div className="esim-featured-collection-card__cover">
                    {thumbnail ? (
                      <img
                        alt={collection.displayName}
                        src={thumbnail}
                        className="esim-featured-collection-card__image"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faLayerGroup} className="esim-featured-collection-card__icon" />
                    )}
                  </div>
                }
              >
                <div className="esim-featured-collection-card__name">{collection.displayName}</div>
                <div className="esim-featured-collection-card__desc">{collection.description || ""}</div>
                <div className="esim-featured-collection-card__footer">
                  {priceLabel && <span className="esim-featured-collection-card__price">{priceLabel}</span>}
                  <span className="esim-featured-collection-card__count">
                    {t("esim.featured_collections_count", { count: collection.esimPagination?.total ?? 0 })}
                  </span>
                </div>
              </Card>
            </Col>
          );
        })}
      </Row>
    </section>
  );
};

export default EsimFeaturedCollections;
